const VP_OPTION_KEYS = {
  SIZE: 'size',
  INSEAM: 'inseam',
};

function vpNormalizeOptionName(name) {
  return String(name || '').toLowerCase().trim();
}

function vpClassifyOptionName(name) {
  const normalized = vpNormalizeOptionName(name);

  if (normalized.includes('inseam') || normalized.includes('length')) {
    return VP_OPTION_KEYS.INSEAM;
  }

  if (normalized.includes('size')) {
    return VP_OPTION_KEYS.SIZE;
  }

  return null;
}

function vpCompareInseamValues(a, b) {
  const numA = parseInt(String(a).replace(/[^\d]/g, ''), 10);
  const numB = parseInt(String(b).replace(/[^\d]/g, ''), 10);

  if (!Number.isNaN(numA) && !Number.isNaN(numB) && numA !== numB) {
    return numA - numB;
  }

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

const ProductData = {
  cache: new Map(),
  inflight: new Map(),

  productUrl(handle) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}products/${encodeURIComponent(handle)}.js`;
  },

  getCached(handle) {
    return this.cache.get(handle) || null;
  },

  has(handle) {
    return this.cache.has(handle);
  },

  clear(handle) {
    if (handle) {
      this.cache.delete(handle);
      this.inflight.delete(handle);
      return;
    }

    this.cache.clear();
    this.inflight.clear();
  },

  async get(handle) {
    if (!handle) {
      throw new Error('[ProductData] Product handle is required');
    }

    if (this.cache.has(handle)) {
      return this.cache.get(handle);
    }

    if (this.inflight.has(handle)) {
      return this.inflight.get(handle);
    }

    const request = fetch(this.productUrl(handle), {
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`[ProductData] Failed to load product "${handle}" (${response.status})`);
        }

        return response.json();
      })
      .then((product) => {
        this.cache.set(handle, product);
        this.inflight.delete(handle);
        return product;
      })
      .catch((error) => {
        this.inflight.delete(handle);
        throw error;
      });

    this.inflight.set(handle, request);
    return request;
  },

  prefetch(handles = []) {
    const uniqueHandles = [...new Set(handles.filter(Boolean))];
    return Promise.allSettled(uniqueHandles.map((handle) => this.get(handle)));
  },

  prefetchFromConfig(config = window.cartUpsellConfig) {
    if (!config?.groups?.length) return Promise.resolve([]);

    const handles = new Set();

    config.groups.forEach((group) => {
      group.products?.forEach((product) => {
        if (product?.handle) handles.add(product.handle);
      });
    });

    return this.prefetch([...handles]);
  },
};

class VariantSelector {
  static getVariantOptionValue(variant, position) {
    if (Array.isArray(variant.options) && variant.options.length >= position) {
      return variant.options[position - 1];
    }

    return variant[`option${position}`] ?? null;
  }

  static trimInseamDisplay(value) {
    const raw = String(value || '');
    const quoteParts = raw.split('"');
    let display = quoteParts[0].trim();

    if (display === raw) {
      display = raw.split(' ')[0].trim().replace(/"/g, '');
    }

    return `${display}"`;
  }

  static resolveOptions(product) {
    const map = {
      [VP_OPTION_KEYS.SIZE]: null,
      [VP_OPTION_KEYS.INSEAM]: null,
    };

    (product?.options || []).forEach((option) => {
      const key = vpClassifyOptionName(option.name);
      if (!key || map[key]) return;

      map[key] = {
        name: option.name,
        position: option.position,
        values: [...(option.values || [])],
      };
    });

    if (map[VP_OPTION_KEYS.INSEAM]?.values?.length) {
      map[VP_OPTION_KEYS.INSEAM].values.sort(vpCompareInseamValues);
    }

    return map;
  }

  static selectionFromCartItem(cartItem) {
    const selection = {
      [VP_OPTION_KEYS.SIZE]: null,
      [VP_OPTION_KEYS.INSEAM]: null,
    };

    if (!cartItem) return selection;

    (cartItem.options_with_values || []).forEach((option) => {
      const key = vpClassifyOptionName(option.name);
      if (key && option.value) {
        selection[key] = option.value;
      }
    });

    return selection;
  }

  static getSelectedByPosition(product, selection = {}) {
    const optionMap = this.resolveOptions(product);
    const selectedByPosition = {};

    Object.entries(selection).forEach(([key, value]) => {
      if (!value || !optionMap[key]) return;
      selectedByPosition[optionMap[key].position] = value;
    });

    return selectedByPosition;
  }

  static getOptionState(product, selection, optionKey, rawValue) {
    const optionMap = this.resolveOptions(product);
    const option = optionMap[optionKey];
    if (!option) return 'unavailable';

    const selectedByPosition = this.getSelectedByPosition(product, selection);
    const position = option.position;
    const variants = product?.variants || [];

    const matchingVariants = variants.filter((variant) => {
      if (this.getVariantOptionValue(variant, position) !== rawValue) return false;

      return Object.entries(selectedByPosition).every(([otherPosition, otherValue]) => {
        if (parseInt(otherPosition, 10) === position) return true;
        return this.getVariantOptionValue(variant, parseInt(otherPosition, 10)) === otherValue;
      });
    });

    if (!matchingVariants.length) return 'unavailable';
    if (matchingVariants.some((variant) => variant.available !== false)) return 'available';
    return 'sold-out';
  }

  static findMatchingVariant(product, selection = {}) {
    const optionMap = this.resolveOptions(product);
    const size = selection[VP_OPTION_KEYS.SIZE];
    const inseam = selection[VP_OPTION_KEYS.INSEAM];

    if (!size || !inseam) return null;
    if (!optionMap[VP_OPTION_KEYS.SIZE] || !optionMap[VP_OPTION_KEYS.INSEAM]) return null;

    return (
      (product?.variants || []).find((variant) => {
        const variantSize = this.getVariantOptionValue(variant, optionMap[VP_OPTION_KEYS.SIZE].position);
        const variantInseam = this.getVariantOptionValue(variant, optionMap[VP_OPTION_KEYS.INSEAM].position);

        return variantSize === size && variantInseam === inseam;
      }) || null
    );
  }

  static buildSelectData(product, selection = {}) {
    const optionMap = this.resolveOptions(product);
    const soldOutLabel = window.variantStrings?.soldOut || 'sold out';
    const result = {};

    [VP_OPTION_KEYS.SIZE, VP_OPTION_KEYS.INSEAM].forEach((key) => {
      const option = optionMap[key];
      if (!option) return;

      result[key] = {
        label: option.name,
        options: option.values.map((rawValue) => {
          const state = this.getOptionState(product, selection, key, rawValue);
          const display =
            key === VP_OPTION_KEYS.INSEAM ? this.trimInseamDisplay(rawValue) : String(rawValue);

          return {
            value: rawValue,
            label: state === 'sold-out' ? `${display} — ${soldOutLabel}` : display,
            disabled: state !== 'available',
            soldOut: state === 'sold-out',
            unavailable: state === 'unavailable',
          };
        }),
      };
    });

    return result;
  }
}

window.VPProductData = ProductData;
window.VPVariantSelector = VariantSelector;
window.VP_OPTION_KEYS = VP_OPTION_KEYS;
